import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Sparkles, Zap, Ticket, ShieldCheck, Truck } from "lucide-react";
import { api, ASSETS, img, DROPS as STATIC_DROPS } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";
import Marquee from "@/components/Marquee";

export default function Landing() {
  // Ship the drops list with the bundle so the Drops section renders instantly
  // (and remains resilient even if the /api/drops endpoint is unreachable).
  const [drops, setDrops] = useState(STATIC_DROPS);
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    (async () => {
      try {
        const dRes = await api.get("/drops");
        if (Array.isArray(dRes.data?.drops) && dRes.data.drops.length) {
          setDrops(dRes.data.drops);
        }
      } catch {
        // Static drops already rendered — nothing to do.
      }
    })();
  }, []);

  useEffect(() => {
    const target = new Date();
    target.setDate(target.getDate() + 14);
    target.setHours(20, 0, 0, 0);
    const tick = () => {
      const diff = Math.max(0, target - new Date());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-obsidian" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={img(ASSETS.helipad, 1600, 82)}
            alt="GrailSwag helipad"
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
          />
          {/* Softer overlay so the painted GrailSwag logo on the helipad is clearly visible while text stays legible */}
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/85 via-obsidian/45 to-obsidian/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/30" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-6 pb-32 lg:pt-32 lg:pb-40 min-h-[85vh] flex flex-col justify-start lg:justify-between">
          <div className="flex items-center gap-3 reveal whitespace-nowrap overflow-hidden">
            <div className="w-6 sm:w-8 h-px bg-imperial shrink-0" />
            <span className="font-mono text-[9px] sm:text-[11px] tracking-[0.22em] sm:tracking-[0.3em] uppercase text-imperial truncate">
              Coming Soon · Chapter I — Grail Season
            </span>
          </div>

          <div className="max-w-4xl reveal reveal-delay-1 mt-6 lg:mt-0">
            <h1 className="font-display text-6xl sm:text-7xl lg:text-[9rem] leading-[0.85] uppercase text-bone headline-shadow">
              Wear the<br />
              <span className="text-imperial">Grail.</span>
            </h1>
            <p className="mt-6 lg:mt-8 text-bone/85 max-w-xl text-base lg:text-lg leading-relaxed">
              Museum-quality streetwear. Limited to <span className="font-mono text-imperial">500 units per design</span>. When it's gone, it's gone — every piece a numbered chapter in the GrailSwag archive.
            </p>
            <div className="mt-8 lg:mt-10 hidden sm:flex flex-col sm:flex-row gap-3">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-cta">
                SHOP THE DROPS <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => scrollTo("golden-ticket")} className="btn-ghost" data-testid="hero-ticket-cta">
                <Ticket className="w-4 h-4" /> GOLDEN TICKET
              </button>
            </div>
          </div>

          {/* Stats strip hidden on mobile so the painted helipad logo stays visible below the hero copy */}
          <div className="reveal reveal-delay-2 hidden lg:grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-bone-10 pt-8 mt-16">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone/40 mb-2">Next Drop In</div>
              <div className="font-mono text-imperial text-lg">
                {String(countdown.d).padStart(2, "0")}D : {String(countdown.h).padStart(2, "0")}H : {String(countdown.m).padStart(2, "0")}M : {String(countdown.s).padStart(2, "0")}S
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone/40 mb-2">Units Per Design</div>
              <div className="font-display text-3xl text-bone">500</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone/40 mb-2">Active Drops</div>
              <div className="font-display text-3xl text-bone">03</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone/40 mb-2">Free U.S. Shipping</div>
              <div className="font-display text-3xl text-bone">$100+</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-imperial text-obsidian py-4 border-y border-obsidian">
        <Marquee>
          {["Limited to 500 units", "Museum-quality print", "Free U.S. shipping $100+", "Member-only drops", "Golden ticket giveaways", "Streetwear grails, curated"].map((t, i) => (
            <span key={i} className="font-mono text-xs tracking-[0.3em] uppercase flex items-center gap-4">
              <Crown className="w-4 h-4" />
              {t}
            </span>
          ))}
        </Marquee>
      </div>

      {/* THE DROPS */}
      <section id="drops" className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36 cv-auto" data-testid="drops-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-imperial mb-3">Chapter Index — 001 / 003</div>
            <h2 className="font-display text-5xl lg:text-7xl uppercase text-bone leading-[0.9]">The Drops</h2>
          </div>
          <p className="max-w-md text-bone/60 text-sm leading-relaxed">
            Three chapters. Nine grails. Each design a limited-edition study in scarcity, streetwear, and cultural artifact.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {drops.map((d, i) => (
            <Link
              to={`/drops/${d.id}`}
              key={d.id}
              className="group relative block reveal"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`drop-card-${d.id}`}
            >
              <div className="img-hover aspect-[3/4] bg-ink border border-bone-10">
                <img src={img(d.hero_image, 800)} alt={d.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-bone bg-obsidian/70 backdrop-blur px-3 py-1.5 border border-bone-20">
                CHAPTER {String(i + 1).padStart(2, "0")}
              </div>
              <div className="pt-5">
                <div className="font-display text-4xl uppercase text-bone group-hover:text-imperial transition-colors">{d.name}</div>
                <p className="text-bone/60 text-sm mt-2 leading-relaxed">{d.tagline}</p>
                <div className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-imperial">
                  Explore Drop <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS removed per client request */}

      {/* GOLDEN TICKET */}
      <section
        id="golden-ticket"
        className="my-24 lg:my-36 relative overflow-hidden bg-imperial text-obsidian grain-overlay"
        data-testid="golden-ticket-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-obsidian/70 mb-4">Members-Only Access</div>
            <h2 className="font-display text-5xl lg:text-7xl uppercase leading-[0.9]">
              The<br />Golden<br />Ticket.
            </h2>
            <p className="mt-6 text-obsidian/80 max-w-lg text-base leading-relaxed">
              Enter our inner circle. Early access to every drop before the public. Monthly Golden Ticket giveaways for free merch. It's not a mailing list — it's a membership.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                { icon: Zap, label: "48-hour early access to every drop" },
                { icon: Crown, label: "Golden Ticket giveaway — free grail every month" },
                { icon: Sparkles, label: "Members-only colorways & restocks" },
                { icon: ShieldCheck, label: "No spam. Ever. Just grails." },
              ].map((row) => (
                <li key={row.label} className="flex items-center gap-3 font-mono text-xs tracking-[0.15em] uppercase">
                  <row.icon className="w-4 h-4 text-crown" />
                  {row.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="bg-obsidian text-bone p-10 lg:p-14 border-2 border-obsidian must-invert">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-bone-10">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone/40">Ticket No.</div>
                  <div className="font-mono text-imperial text-lg">GS-{Math.floor(1000 + Math.random() * 9000)}</div>
                </div>
                <Ticket className="w-10 h-10 text-imperial" />
              </div>
              <div className="font-display text-3xl uppercase mb-2 text-bone">Join The<br /><span className="text-imperial">Grail Society</span></div>
              <p className="text-bone/60 text-sm mb-6">One email. Zero spam. Everything you want to know first.</p>
              <NewsletterForm source="golden-ticket" />
            </div>
          </div>
        </div>
      </section>

      {/* GRAILS IN THE WILD — Lambo + Billboard */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 cv-auto" data-testid="in-the-wild-section">
        <div className="mb-10">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-imperial mb-3">Grails In The Wild</div>
          <h3 className="font-display text-4xl lg:text-6xl uppercase text-bone leading-[0.9]">
            The Culture,<br />on the Move.
          </h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div className="img-hover border border-bone-10 aspect-[16/10] bg-ink">
            <img src={img(ASSETS.lambo, 1200)} alt="GrailSwag Lamborghini" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="img-hover border border-bone-10 aspect-[16/10] bg-ink">
            <img src={img(ASSETS.billboard, 1200)} alt="GrailSwag NYC Billboard" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
          </div>
        </div>
        {/* Manifesto copy sits BELOW the billboard imagery (not overlaid) */}
        <div className="mt-10 lg:mt-14 max-w-4xl">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-imperial mb-3">The Manifesto</div>
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase text-bone leading-[1.05]">
            Join the fraternity of grail collectors.
            <span className="text-bone/60"> Stand out in every crowd. </span>
            <span className="text-imperial">Wear the universal language</span>
            <span className="text-bone/60"> of luxury, art, and collector royalty.</span>
          </p>
          <p className="mt-5 text-bone/60 max-w-2xl leading-relaxed">
            This is not merch — it's membership. Every stitch numbered, every drop museum-grade, every wearer inducted into a lifestyle brotherhood that speaks one language across every zip code, tarmac, and skyline on earth.
          </p>
        </div>
      </section>

      {/* ABOUT / CULTURE */}
      <section id="about" className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24 cv-auto" data-testid="about-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-imperial mb-3">The House</div>
            <h3 className="font-display text-4xl lg:text-6xl uppercase text-bone leading-[0.9]">
              Scarcity is<br />the new luxury.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-bone/70 leading-relaxed">
            <p>
              GrailSwag lives where auction house meets skate shop. We take cues from graffiti-crowned canvases, hardwood mythology, museum-lit galleries, and card-shop legends — and stitch it into wearable relics.
            </p>
            <p>
              Every drop is capped at <span className="text-imperial font-mono">500 units</span> across every size and colorway combined. Sold out means sold out — no restocks, no re-issues. What you wear today becomes the archive tomorrow.
            </p>
            <div className="grid grid-cols-3 gap-6 pt-4 border-t border-bone-10">
              <div>
                <Truck className="w-5 h-5 text-imperial mb-2" />
                <div className="font-mono text-[10px] tracking-widest uppercase text-bone/50">Free U.S. Ship</div>
                <div className="font-display text-2xl text-bone">$100+</div>
              </div>
              <div>
                <Crown className="w-5 h-5 text-imperial mb-2" />
                <div className="font-mono text-[10px] tracking-widest uppercase text-bone/50">Limited</div>
                <div className="font-display text-2xl text-bone">500 UNITS</div>
              </div>
              <div>
                <Sparkles className="w-5 h-5 text-imperial mb-2" />
                <div className="font-mono text-[10px] tracking-widest uppercase text-bone/50">Museum-grade</div>
                <div className="font-display text-2xl text-bone">PRINT</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
